import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { useTheme } from '../../context/ThemeContext';
import { resultService, classService, subjectService, studentService } from '../../services/api';

const TeacherResults: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [results, setResults] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [formData, setFormData] = useState({
    studentId: '',
    subjectId: '',
    examType: 'mid-term',
    term: 'First Term',
    marksObtained: 0,
    totalMarks: 100,
    grade: '',
    remarks: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchClassStudents(selectedClass);
    }
  }, [selectedClass]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resultsRes, classesRes, subjectsRes] = await Promise.all([
        resultService.getAll(),
        classService.getAll(),
        subjectService.getAll()
      ]);
      
      // Handle different response structures
      const resultsData = Array.isArray(resultsRes.data) 
        ? resultsRes.data 
        : (resultsRes.data?.results || []);
      const classesData = Array.isArray(classesRes.data) 
        ? classesRes.data 
        : (classesRes.data?.classes || []);
      const subjectsData = Array.isArray(subjectsRes.data) 
        ? subjectsRes.data 
        : (subjectsRes.data?.subjects || []);
        
      setResults(resultsData);
      setClasses(classesData);
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      alert('Failed to load results');
      setResults([]);
      setClasses([]);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassStudents = async (classId: string) => {
    try {
      const response = await studentService.getAll({ class: classId });
      setStudents(response.data?.students || []);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setStudents([]);
    }
  };

  const calculateGrade = (marks: number, total: number) => {
    const percentage = (marks / total) * 100;
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    return 'F';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const grade = calculateGrade(formData.marksObtained, formData.totalMarks);
      await resultService.create({ ...formData, grade });
      alert('Result uploaded successfully!');
      setShowForm(false);
      setFormData({
        studentId: '',
        subjectId: '',
        examType: 'mid-term',
        term: 'First Term',
        marksObtained: 0,
        totalMarks: 100,
        grade: '',
        remarks: ''
      });
      fetchData();
    } catch (error: any) {
      console.error('Failed to upload result:', error);
      alert(error.response?.data?.message || 'Failed to upload result');
    }
  };

  // Filter and sort results
  const filteredResults = results
    .filter(result => {
      const studentName = result.student?.user ? 
        `${result.student.user.firstName} ${result.student.user.lastName}`.toLowerCase() : '';
      const subjectName = result.subject?.name?.toLowerCase() || '';
      const matchesSearch = studentName.includes(searchTerm.toLowerCase()) || 
                           subjectName.includes(searchTerm.toLowerCase());
      const matchesSubject = !filterSubject || result.subjectId === filterSubject;
      return matchesSearch && matchesSubject;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = a.student?.user ? `${a.student.user.firstName} ${a.student.user.lastName}` : '';
          const nameB = b.student?.user ? `${b.student.user.firstName} ${b.student.user.lastName}` : '';
          return nameA.localeCompare(nameB);
        case 'marks':
          return (b.marksObtained / b.totalMarks) - (a.marksObtained / a.totalMarks);
        case 'grade':
          return (a.grade || '').localeCompare(b.grade || '');
        case 'date':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

  return (
    <Layout title="Upload Results">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
        <div className="mb-6 flex justify-between items-center">
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Results Management
          </h2>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            {showForm ? 'Cancel' : '+ Upload Result'}
          </button>
        </div>

        {/* Search and Filter Section */}
        {!showForm && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Search Student/Subject
              </label>
              <input
                type="text"
                placeholder="Search by name or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full px-4 py-2 rounded border ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Filter by Subject
              </label>
              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                className={`w-full px-4 py-2 rounded border ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">All Subjects</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={`w-full px-4 py-2 rounded border ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="date">Latest First</option>
                <option value="name">Student Name (A-Z)</option>
                <option value="marks">Highest Marks</option>
                <option value="grade">Grade (A-F)</option>
              </select>
            </div>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Class *
                </label>
                <select
                  required
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Select Class</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Student *
                </label>
                <select
                  required
                  value={formData.studentId}
                  onChange={(e) => setFormData({...formData, studentId: e.target.value})}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  disabled={!selectedClass}
                >
                  <option value="">Select Student</option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.firstName} {student.lastName} - {student.rollNumber}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Subject *
                </label>
                <select
                  required
                  value={formData.subjectId}
                  onChange={(e) => setFormData({...formData, subjectId: e.target.value})}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Exam Type *
                </label>
                <select
                  value={formData.examType}
                  onChange={(e) => setFormData({...formData, examType: e.target.value})}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="unit-test">Unit Test</option>
                  <option value="mid-term">Mid Term</option>
                  <option value="final">Final</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Marks Obtained *
                </label>
                <input
                  type="number"
                  required
                  value={formData.marksObtained}
                  onChange={(e) => setFormData({...formData, marksObtained: parseFloat(e.target.value)})}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total Marks *
                </label>
                <input
                  type="number"
                  required
                  value={formData.totalMarks}
                  onChange={(e) => setFormData({...formData, totalMarks: parseFloat(e.target.value)})}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div className="md:col-span-2">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Remarks
                </label>
                <textarea
                  rows={3}
                  value={formData.remarks}
                  onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Upload Result
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <th className={`px-4 py-3 text-left ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Student</th>
                  <th className={`px-4 py-3 text-left ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Subject</th>
                  <th className={`px-4 py-3 text-left ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Exam Type</th>
                  <th className={`px-4 py-3 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Marks</th>
                  <th className={`px-4 py-3 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Grade</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">
                      {results.length === 0 ? 'No results found' : 'No results match your filters'}
                    </td>
                  </tr>
                ) : (
                  filteredResults.map((result) => {
                    const studentName = result.student?.user ? 
                      `${result.student.user.firstName} ${result.student.user.lastName}` : 
                      'N/A';
                    const subjectName = result.subject?.name || 'N/A';
                    
                    return (
                      <tr key={result.id} className={isDarkMode ? 'border-gray-700' : 'border-gray-200 border-t'}>
                        <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{studentName}</td>
                        <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{subjectName}</td>
                        <td className={`px-4 py-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{result.examType}</td>
                        <td className={`px-4 py-3 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {result.marksObtained}/{result.totalMarks}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 rounded bg-blue-100 text-blue-800">
                            {result.grade}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TeacherResults;
